import { fetchAuthSession } from 'aws-amplify/auth';

// Cognito errors that mean the refresh token itself is dead (expired, revoked,
// or the user is gone/disabled). Only these end the session — anything else
// (network blips, laptop wake, Cognito hiccups) is transient and must NEVER
// clear stored tokens, or we destroy the refresh token and log the user out.
const TERMINAL_AUTH_ERRORS = new Set([
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
]);

class SessionExpiredError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'SessionExpiredError';
    this.isSessionExpired = true;
    this.cause = cause;
  }
}

class TokenRefreshManager {
  constructor() {
    this.refreshTimer = null;
    this.refreshPromise = null; // shared in-flight refresh — concurrent callers await the same one
    this.refreshPromiseForced = false; // whether the in-flight refresh is a forced one
    this.sessionExpired = false; // sticky after a terminal refresh error, until a new session appears
    this.refreshThreshold = 5 * 60 * 1000; // refresh when within 5 minutes of expiration
    this.wakeListenersAttached = false;
  }

  async initializeTokenRefresh() {
    this.attachWakeListeners();
    try {
      await this.checkAndRefreshToken();
    } catch (error) {
      console.error('Failed to initialize token refresh:', error);
      // Don't throw error here - let the app continue and handle auth failures elsewhere
    }
    this.scheduleNextRefresh();
  }

  // Browsers throttle/suspend timers in background tabs and during sleep, so
  // the interval alone can leave us holding an expired token when the machine
  // wakes. Refresh proactively when the tab becomes visible or network returns.
  attachWakeListeners() {
    if (this.wakeListenersAttached || typeof window === 'undefined') {
      return;
    }
    this.wakeListenersAttached = true;

    const refreshOnWake = () => {
      this.checkAndRefreshToken().catch(() => {
        // Transient failures are retried by the timer; terminal ones are
        // surfaced on the next API call.
      });
    };

    window.addEventListener('online', refreshOnWake);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refreshOnWake();
      }
    });
  }

  // Returns a session whose tokens are safe to use. Concurrent callers share a
  // single in-flight check so nobody ever observes a half-finished refresh.
  checkAndRefreshToken({ force = false } = {}) {
    if (this.refreshPromise) {
      if (!force || this.refreshPromiseForced) {
        return this.refreshPromise;
      }
      // A forced caller (server just rejected the token) must not be satisfied
      // by a weaker in-flight check that may deem the same token locally valid.
      // Wait it out, then run forced — concurrent forced callers coalesce onto
      // the forced run via the recursion.
      return this.refreshPromise
        .catch(() => {})
        .then(() => this.checkAndRefreshToken({ force: true }));
    }
    this.refreshPromiseForced = force;
    this.refreshPromise = this.doCheckAndRefresh({ force }).finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  async doCheckAndRefresh({ force }) {
    let session;
    try {
      session = await fetchAuthSession({ forceRefresh: false });
    } catch (error) {
      // When the access token is already expired, Amplify refreshes inside
      // this call — so a dead refresh token surfaces HERE, not just in the
      // forced refresh below. Classify it the same way or the session-expired
      // redirect never happens and the app spins on 401s.
      if (TERMINAL_AUTH_ERRORS.has(error.name)) {
        console.error('Refresh token is no longer valid:', error);
        this.sessionExpired = true;
        this.clearTokensFromLocalStorage();
        throw new SessionExpiredError('Refresh token is no longer valid', error);
      }
      // Transient failure — stored tokens stay untouched.
      throw error;
    }
    const accessToken = session.tokens?.accessToken;

    if (!accessToken) {
      // After a terminal error cleared the session, later callers (e.g. the
      // 401-retry's forced refresh) land here — keep reporting session-expired
      // so the login redirect still fires.
      if (this.sessionExpired) {
        throw new SessionExpiredError('Session ended — refresh token was no longer valid');
      }
      // Not signed in (or Amplify has no session yet). Not a reason to touch
      // storage — just report it.
      throw new Error('No access token found');
    }
    this.sessionExpired = false;

    const timeUntilExpiration = accessToken.payload.exp * 1000 - Date.now();

    if (!force && timeUntilExpiration > this.refreshThreshold) {
      return session;
    }

    try {
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      if (refreshedSession.tokens?.accessToken) {
        // Amplify persists the refreshed tokens to localStorage itself; no
        // manual writes needed.
        return refreshedSession;
      }
      // Refresh produced no tokens. If the current token still works, keep
      // going with it; otherwise the refresh token is spent.
      if (timeUntilExpiration > 0) {
        return session;
      }
      throw new SessionExpiredError('Token refresh returned no tokens');
    } catch (error) {
      if (error.isSessionExpired) {
        this.sessionExpired = true;
        this.clearTokensFromLocalStorage();
        throw error;
      }
      if (TERMINAL_AUTH_ERRORS.has(error.name)) {
        console.error('Refresh token is no longer valid:', error);
        this.sessionExpired = true;
        this.clearTokensFromLocalStorage();
        throw new SessionExpiredError('Refresh token is no longer valid', error);
      }
      // Transient failure (network down, Cognito 5xx, ...). Keep the stored
      // tokens — the refresh token is still good and a later attempt will
      // succeed. If the current access token hasn't actually expired yet, let
      // the caller use it.
      console.warn('Transient token refresh failure, will retry:', error);
      if (timeUntilExpiration > 0) {
        return session;
      }
      throw error;
    }
  }

  clearTokensFromLocalStorage() {
    try {
      // Clear Cognito tokens from localStorage, but keep the remembered-device
      // keys — only the session is invalid here, and wiping deviceKey would
      // force a fresh MFA challenge on the next sign-in even on a trusted device.
      const keysToRemove = Object.keys(localStorage)
        .filter(key => key.includes('CognitoIdentityServiceProvider') || key.endsWith('.accessToken') || key.endsWith('.idToken') || key.endsWith('.refreshToken'))
        .filter(key => !/\.(deviceKey|deviceGroupKey|randomPasswordKey)$/.test(key));

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Failed to clear tokens from localStorage:', error);
    }
  }

  scheduleNextRefresh() {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Check every minute; the chain must survive failures or background
    // refresh silently stops until the next full page load.
    this.refreshTimer = setTimeout(async () => {
      try {
        await this.checkAndRefreshToken();
      } catch (error) {
        console.error('Scheduled token refresh failed:', error);
      }
      this.scheduleNextRefresh();
    }, 60 * 1000);
  }

  stopTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Ensure the session is valid before an API call. Pass { force: true } to
  // refresh regardless of expiration (e.g. after a 401).
  async ensureValidToken(options) {
    return this.checkAndRefreshToken(options);
  }
}

// Create singleton instance
const tokenRefreshManager = new TokenRefreshManager();

export default tokenRefreshManager;
